import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  BASE_URL,
  getRefreshToken,
  loadStoredRefresh,
  setTokens,
} from "@/api/client";

export type AdminUser = {
  id: string;
  display_name: string;
  email: string | null;
  is_superuser: boolean;
};

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: AdminUser };

type AuthContextValue = {
  state: AuthState;
  requestMagicLink: (email: string) => Promise<void>;
  redeemMagicLink: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

async function postJson<T>(path: string, body: unknown): Promise<T> {
  // Same `VITE_API_URL` resolution as the openapi-fetch client uses.
  // Same-origin dev / mounted prod ⇒ BASE_URL='' ⇒ relative path.
  // Split-origin staging ⇒ absolute API URL.
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: string | undefined;
    try {
      const data = (await res.json()) as { detail?: string };
      detail = data?.detail;
    } catch {
      /* swallow */
    }
    throw new Error(detail ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

type SignInResponse = {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    display_name: string;
    email: string | null;
    is_superuser: boolean;
  };
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  const finishSignIn = useCallback((res: SignInResponse) => {
    setTokens(res.access_token, res.refresh_token);
    setState({
      status: "authenticated",
      user: {
        id: res.user.id,
        display_name: res.user.display_name,
        email: res.user.email,
        is_superuser: res.user.is_superuser,
      },
    });
  }, []);

  // Bootstrap: if a refresh token is in localStorage, exchange it for a
  // fresh access token + user. Otherwise mark as anonymous.
  useEffect(() => {
    const stored = loadStoredRefresh();
    if (!stored) {
      setState({ status: "anonymous" });
      return;
    }
    void (async () => {
      try {
        const res = await postJson<SignInResponse>("/api/v1/auth/refresh", {
          refresh_token: stored,
        });
        if (!res.user.is_superuser) {
          setTokens(null, null);
          setState({ status: "anonymous" });
          return;
        }
        finishSignIn(res);
      } catch {
        setTokens(null, null);
        setState({ status: "anonymous" });
      }
    })();
  }, [finishSignIn]);

  const requestMagicLink = useCallback(async (email: string) => {
    await fetch(`${BASE_URL}/api/v1/auth/magic-link/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        app_variant: "web",
        require_superuser: true,
        redirect_path: "/admin/auth/redeem",
      }),
    });
  }, []);

  const redeemMagicLink = useCallback(
    async (token: string) => {
      const res = await postJson<SignInResponse>(
        "/api/v1/auth/magic-link/redeem",
        { token },
      );
      finishSignIn(res);
    },
    [finishSignIn],
  );

  const signOut = useCallback(async () => {
    const refresh = getRefreshToken();
    if (refresh) {
      await fetch("/api/v1/auth/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
      }).catch(() => {});
    }
    setTokens(null, null);
    setState({ status: "anonymous" });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, requestMagicLink, redeemMagicLink, signOut }),
    [state, requestMagicLink, redeemMagicLink, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
